import React, { useState, useEffect, useRef, useContext } from 'react';
import { Box, Button, CircularProgress, Collapse, FormControlLabel, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useHistory } from 'react-router-dom';
import { checkUser, proceedPaySafePayment } from '../../services/PaySafeService';
import api from '../../services/api';
import useAcquisition from '../Acquisition';
import { AuthContext } from '../../contexts/AuthContext';
import { DataContext } from '../../contexts/DataContext';
import MainCheckbox from '../MainCheckbox';
import './PaymentFormPaysafe.css';

/**
 * Generates a UUID v4 string.
 */
function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0;
        var v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

const PaymentFormPaySafe = ({ ctaText, redirectUrl }) => {
    const acquisition = useAcquisition();
    const { authMethods } = useContext(AuthContext);
    const { user } = useContext(DataContext);
    const history = useHistory();

    const [isLoading, setIsLoading] = useState(false);
    const [cardHolderName, setCardHolderName] = useState('');
    const [instance, setInstance] = useState(null);
    const [error, setError] = useState(null);
    const [openError, setOpenError] = useState(false);
    const creditCardFormRef = useRef(null);
    const instanceExists = useRef(false);
    const [cguError, setCGUError] = useState(null);

    const [cardHolderError, setCardHolderError] = useState(false);
    const [cardNumberError, setCardNumberError] = useState(false);
    const [cardExpirationError, setCardExpirationError] = useState(false);
    const [cardCVCError, setCardCVCError] = useState(false);
    const [amount, setAmount] = useState(99);
    const [merchantRefNum, setMerchantRefNum] = useState(uuidv4());

    useEffect(() => {
        const checkUserGlobal = async () => {
            var res = await checkUser({ email: user.email });
            if (res.existAndIsSubscribed) {
                setError("Un compte actif existe déjà avec cet email. Connectez-vous puis allez sur l'onglet 'Nouveau KBIS' pour générer un nouveau KBIS.");
                setOpenError(true);
            } else {
                setAmount(res.amount);
                if (res.merchantRefNum != null && res.merchantRefNum !== undefined && res.merchantRefNum !== "") {
                    setMerchantRefNum(res.merchantRefNum);
                }
            }
        };

        const initializePaysafe = async () => {
            if (instance || isLoading || !creditCardFormRef.current) {
                return;
            }

            if (window.paysafe) {
                if (instanceExists.current) {
                    return;
                }
                instanceExists.current = true;
                setIsLoading(true);
                const apiKey = process.env.REACT_APP_PAYSAFE_API_KEY;
                const accountId = parseInt(process.env.REACT_APP_PAYSAFE_ACCOUNT_ID);
                const simpleOptions = {
                    currencyCode: 'EUR',
                    environment: process.env.NODE_ENV === 'production' ? 'LIVE' : 'TEST',
                    accounts: {
                        default: accountId,
                    },
                    fields: {
                        cardNumber: { selector: '#card-number', placeholder: 'Numéro de carte' },
                        expiryDate: { selector: '#expiration-date', placeholder: 'MM / AA' },
                        cvv: { selector: '#cvv', placeholder: 'CVC' },
                    },
                };
                window.paysafe.fields.setup(apiKey, simpleOptions).then((currInstance) => {
                    currInstance.show();
                    setIsLoading(false);
                    setInstance(currInstance);
                });
            } else {
                console.error('Paysafe SDK is not available after loading');
                setError('Le SDK Paysafe n\'a pas pu être chargé.');
                setIsLoading(false);
            }
        };

        checkUserGlobal();
        initializePaysafe();
    }, [creditCardFormRef]); // eslint-disable-line react-hooks/exhaustive-deps

    const checkFieldIsEmpty = (field) => {
        if (!instance) return false;
        return instance.fields[field] ? instance.fields[field].isEmpty() : false;
    };

    const checkFieldIsValid = (field) => {
        if (!instance) return false;
        return instance.fields[field] ? instance.fields[field].isValid() : false;
    };

    const checkAllFieldsValid = () => {
        setCGUError(null);
        setCardNumberError(false);
        setCardExpirationError(false);
        setCardCVCError(false);
        setCardHolderError(false);

        let errorLocal = false;
        if (!window.acceptedTerms) {
            setCGUError("Les conditions générales d'utilisation doivent être acceptées.");
            errorLocal = true;
        }

        if (cardHolderName == null || cardHolderName === undefined || cardHolderName.trim().length < 2) {
            setCardHolderError(true);
            errorLocal = true;
        }
        if (errorLocal) {
            return false;
        }

        if (!instance) return false;
        return instance.areAllFieldsValid();
    };

    const [formData, setFormData] = useState({
        acceptedTerms: false,
    });

    const setAcceptedTermsChecked = (field) => (event) => {
        setFormData((prevState) => ({
            ...prevState,
            [field]: event.target.checked,
        }));
        window.acceptedTerms = event.target.checked;
    };

    const getZipCode = () => {
        if (user.postalCode != null && user.postalCode !== undefined && user.postalCode !== "") {
            return user.postalCode;
        } else {
            return "75000";
        }
    };

    const tokenizeMutation = () => {
        if (!instance) {
            return;
        }
        var zipCode = getZipCode();
        console.log(cardHolderName);
        const tokenizeOptions = {
            amount: amount,
            openAs: 'IFRAME',
            transactionType: 'PAYMENT',
            merchantRefNum: uuidv4(),
            paymentType: 'CARD',
            threeDs: {
                profile: {
                    firstName: user.firstname || user.firstName,
                    lastName: user.lastname || user.lastName,
                    email: user.email,
                },
                deviceChannel: "BROWSER",
                useThreeDSecureVersion2: true,
                authenticationPurpose: "RECURRING_TRANSACTION",
                maxAuthorizationsForInstalmentPayment: 999,
                billingCycle: {
                    endDate: "2030-01-01",
                    frequency: 1,
                },
                requestorChallengePreference: "CHALLENGE_MANDATED",
            },
            customerDetails: {
                holderName: cardHolderName,
                billingDetails: {
                    country: "FR",
                    zip: zipCode,
                },
            },
        };
        console.log(tokenizeOptions);
        instance.tokenize(tokenizeOptions)
            .then((result) => {
                const proceedContent = {
                    email: user.email,
                    origin: acquisition ? acquisition.origin : '',
                    referrer: acquisition ? acquisition.referrer : '',
                    ip: acquisition ? acquisition.ip : '',
                    paymentId: merchantRefNum,
                    cardHolder: cardHolderName,
                    country: "fr",
                    paymentToken: result.token,
                    paymentReference: merchantRefNum,
                };
                proceedPaySafePayment(proceedContent)
                    .then((data) => {
                        if (data.existAndIsSubscribed) {
                            setError("Un compte actif existe déjà avec cet email.");
                            setOpenError(true);
                        } else {
                            api.post("/Token",
                                "grant_type=" + data.grant_type + "&username=" + data.username + "&password=" + data.password,
                                {
                                    headers: {
                                        'Content-Type': 'application/x-www-form-urlencoded',
                                    },
                                }
                            ).then((tokenObject) => {
                                authMethods.login(data.user, tokenObject.data.access_token, tokenObject.data.access_token, data.isSubscribed);
                                history.push("/accueil/confirmation");
                            }).catch((err) => {
                                console.log(err);
                                setIsLoading(false);
                                setError("Une erreur s'est produite.");
                                setOpenError(true);
                            });
                        }
                    })
                    .catch((err) => {
                        if (
                            err.response &&
                            err.response.status === 403 &&
                            err.response.data &&
                            err.response.data.response === "Fraud"
                        ) {
                            setError("Banque bloquée suite à des fraudes à répétition. Des procédures sont en cours, veuillez nous contacter pour plus d'informations.");
                        } else {
                            setError("Une erreur s'est produite lors du paiement.");
                        }
                        setOpenError(true);
                        setIsLoading(false);
                    });
            })
            .catch((err) => {
                console.error('Tokenization error:', err);
                setOpenError(true);
                setError("Une erreur s'est produite lors du paiement.");
                setIsLoading(false);
            });
    };

    const handlePay = async () => {
        setIsLoading(true);
        if (checkAllFieldsValid()) {
            tokenizeMutation();
        } else {
            setIsLoading(false);

            if (checkFieldIsEmpty('cardNumber') || !checkFieldIsValid('cardNumber')) {
                setCardNumberError(true);
            }
            if (checkFieldIsEmpty('expiryDate') || !checkFieldIsValid('expiryDate')) {
                setCardExpirationError(true);
            }
            if (checkFieldIsEmpty('cvv') || !checkFieldIsValid('cvv')) {
                setCardCVCError(true);
            }
        }
    };

    const handleCardHolderNameChange = (e) => {
        setCardHolderName(e.target.value);
    };

    const goToCGU = (event) => {
        event.preventDefault();
        history.push("/accueil/conditions-generales-utilisation");
    };

    const goToPrivacyPolicy = () => {
        history.push("/accueil/politique-de-confidentialite");
    };

    return (
        <div>
            <Collapse in={openError}>
                <Alert
                    style={{ marginBottom: "15px" }}
                    severity="error"
                    action={
                        <i
                            className="fas fa-times"
                            style={{ color: "inherit", cursor: "pointer" }}
                            onClick={() => {
                                setOpenError(false);
                                setError(null);
                                if (instance) instance.resetCardDetails();
                                setIsLoading(false);
                            }}
                        ></i>
                    }
                >
                    {error}
                </Alert>
            </Collapse>
            <Collapse in={cguError != null}>
                <Alert
                    style={{ marginBottom: "15px" }}
                    severity="error"
                    action={
                        <i
                            className="fas fa-times"
                            style={{ color: "inherit", cursor: "pointer" }}
                            onClick={() => setCGUError(null)}
                        ></i>
                    }
                >
                    {cguError}
                </Alert>
            </Collapse>

            {openError && (
                <Button
                    variant="contained"
                    color="primary"
                    className="cta"
                    onClick={() => {
                        setOpenError(false);
                        setError(null);
                        if (instance) instance.resetCardDetails();
                        setIsLoading(false);
                    }}
                >
                    Réessayer
                </Button>
            )}

            {/* Card icons — mobile */}
            <Grid container direction="row" spacing={1} className="display-mobile card-icon-div-2">
                <Grid item><div className="card-icon-container-2"><img src="/img/master-card-clean.svg" className="icon-card-2" alt="Mastercard" /></div></Grid>
                <Grid item><div className="card-icon-container-2"><img src="/img/visa.svg" className="icon-card-2" alt="Visa" /></div></Grid>
                <Grid item><div className="card-icon-container-2"><img src="/img/american-express.svg" className="icon-card-2" alt="American Express" /></div></Grid>
            </Grid>

            <Box className="paymenFormContainer">
                {/* Card icons — desktop */}
                <div className="total-processing-card-icons-container">
                    <Grid item className="display-desktop total-processing-card-icons">
                        <Grid container direction="row" spacing={1}>
                            <Grid item><div className="card-icon-container-2"><img src="/img/master-card-clean.svg" className="icon-card-2" alt="Mastercard" /></div></Grid>
                            <Grid item><div className="card-icon-container-2"><img src="/img/visa.svg" className="icon-card-2" alt="Visa" /></div></Grid>
                            <Grid item><div className="card-icon-container-2"><img src="/img/american-express.svg" className="icon-card-2" alt="American Express" /></div></Grid>
                        </Grid>
                    </Grid>
                </div>

                <div ref={creditCardFormRef} className="paysafe-fields-wrapper" style={{ marginTop: 5 }}>
                    {/* Card Number */}
                    <div className="paysafe-field-group paysafe-cardnumber-group">
                        <label className="paysafe-label">Entrez votre numéro de carte</label>
                        <div id="card-number" className="paysafe-hosted-field" />
                        {cardNumberError && (
                            <div className="paysafe-field-error">Le numéro de carte est invalide.</div>
                        )}
                    </div>

                    {/* Expiry + CVC */}
                    <div className="paysafe-row">
                        <div className="paysafe-field-group paysafe-expiry">
                            <label className="paysafe-label">Date de péremption</label>
                            <div id="expiration-date" className="paysafe-hosted-field" />
                            {cardExpirationError && (
                                <div className="paysafe-field-error">La date d'expiration est invalide.</div>
                            )}
                        </div>
                        <div className="paysafe-field-group paysafe-cvv">
                            <label className="paysafe-label">CVC</label>
                            <div id="cvv" className="paysafe-hosted-field" />
                            {cardCVCError && (
                                <div className="paysafe-field-error">Le code CVC est invalide.</div>
                            )}
                        </div>
                    </div>

                    {/* Card Holder Name */}
                    <div className="paysafe-field-group paysafe-cardholder-group">
                        <label className="paysafe-label">Nom du titulaire</label>
                        <input
                            id="card-holder"
                            type="text"
                            name="lastname"
                            placeholder="Nom du titulaire"
                            value={cardHolderName}
                            onChange={handleCardHolderNameChange}
                            className="paysafe-input"
                        />
                        {cardHolderError && (
                            <div className="paysafe-field-error">Le nom du titulaire de la carte est obligatoire.</div>
                        )}
                    </div>
                </div>
            </Box>

            <FormControlLabel
                className="checkbox-input-payment"
                control={
                    <MainCheckbox
                        checked={formData.acceptedTerms}
                        onChange={setAcceptedTermsChecked("acceptedTerms")}
                        name="checkedB"
                        color="primary"
                    />
                }
                label={
                    <span className="label-input-small-2">
                        Je reconnais avoir pris connaissance et accepte les{' '}
                        <span className="link" onClick={goToCGU}>conditions générales d'utilisation</span>{' '}
                        et la{' '}
                        <span className="link" onClick={goToPrivacyPolicy}>politique de confidentialité</span>,
                        d'utilisation et d'accès à la plateforme.
                    </span>
                }
            />

            <div className="paysafe-submit-row">
                {isLoading ? (
                    <CircularProgress size={32} />
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        className="cta"
                        onClick={handlePay}
                        disabled={error != null || isLoading}
                    >
                        {ctaText || "Télécharger mon KBIS"}
                        <img src="/img/arrow-right2.svg" alt="" />
                    </Button>
                )}
            </div>
        </div>
    );
};

export default PaymentFormPaySafe;
